import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    afterNextRender,
    computed,
    contentChildren,
    effect,
    forwardRef,
    input,
    isDevMode,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25CardCarouselItemDirective } from "./s25.card.carousel.item.directive";

/**
 * Generic horizontal snap-scrolling card carousel.
 *
 * Usage:
 * ```html
 * <s25-ng-card-carousel ariaLabel="System notices" itemNoun="notice">
 *   @for (item of items(); track item.id) {
 *     <my-card s25-ng-card-carousel-item [item]="item" />
 *   }
 *   <p s25-ng-card-carousel-empty>No items.</p>
 * </s25-ng-card-carousel>
 * ```
 *
 * Every direct child in the default slot MUST carry [s25-ng-card-carousel-item] so that
 * role=list / role=listitem parentage is maintained and card widths are applied correctly.
 * Scoped CSS cannot reach projected nodes; the directive's host bindings are the only
 * mechanism that survives emulated view encapsulation.
 *
 * Container role mutates between "region" (0–1 items) and "list" (2+ items). AT support for
 * live role changes is unreliable; the consumer's existing polite announcement carries the
 * change. Do NOT compensate with a live region — it would re-announce on every navigation.
 *
 * IMPORTANT: Never add auto-advance. A timed scroll violates WCAG 2.2.2 and would make this
 * component unusable for anyone who cannot move quickly.
 */
@TypeManagerDecorator("s25-ng-card-carousel")
@Component({
    selector: "s25-ng-card-carousel",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent],
    host: {
        class: "s25-card-carousel-host",
        "[class.s25-card-carousel-host--fill-height]": "fillHeight()",
    },
    template: `
        <div class="s25-card-carousel">
            <div
                #scrollContainer
                class="s25-card-carousel__scroll"
                [class.s25-card-carousel__scroll--single]="!isMultiple()"
                [class.reduced-motion]="reducedMotion()"
                [attr.role]="containerRole()"
                [attr.aria-label]="ariaLabel()"
                [attr.tabindex]="containerTabIndex()"
            >
                <ng-content />
                @if (isEmpty()) {
                    <div class="s25-card-carousel__empty">
                        <ng-content select="[s25-ng-card-carousel-empty]" />
                    </div>
                }
            </div>
            @if (showControls()) {
                <div class="s25-card-carousel__controls">
                    <button
                        type="button"
                        class="s25-card-carousel__arrow s25-card-carousel__arrow--prev"
                        [attr.aria-label]="previousAriaLabel()"
                        (click)="scrollByCards(-1)"
                    >
                        <s25-ng-icon [type]="'arrowLeft'" [size]="20" [ariaLabel]="false" />
                    </button>
                    <button
                        type="button"
                        class="s25-card-carousel__arrow s25-card-carousel__arrow--next"
                        [attr.aria-label]="nextAriaLabel()"
                        (click)="scrollByCards(1)"
                    >
                        <s25-ng-icon [type]="'arrowRight'" [size]="20" [ariaLabel]="false" />
                    </button>
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        :host(.s25-card-carousel-host--fill-height) {
            display: flex;
            flex-direction: column;
        }

        .s25-card-carousel {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 0.5rem;
            min-width: 0;
        }

        :host(.s25-card-carousel-host--fill-height) .s25-card-carousel {
            flex: 1;
            min-height: 0;
        }

        .s25-card-carousel__scroll {
            --s25-card-carousel-item-width: min(32rem, calc(100% - 3rem));
            display: flex;
            flex: 1;
            gap: 1rem;
            min-width: 0;
            overflow-x: auto;
            scroll-snap-type: x proximity;
            scroll-behavior: smooth;
            touch-action: pan-x;
            overscroll-behavior-x: contain;
            scrollbar-width: thin;
            padding-block: 0.25rem;
        }

        .s25-card-carousel__scroll.reduced-motion {
            scroll-behavior: auto;
        }

        .s25-card-carousel__scroll:focus-visible {
            outline: none;
            box-shadow: var(--shadow--inner-focus);
            border-radius: 6px;
        }

        .s25-card-carousel__scroll--single {
            --s25-card-carousel-item-width: 100%;
        }

        @media (max-width: 930px) {
            .s25-card-carousel__scroll {
                --s25-card-carousel-item-width: calc(100% - 2rem);
            }

            :host(.s25-card-carousel-host--fill-height) .s25-card-carousel__scroll {
                flex: 1;
                min-height: 0;
            }

            .s25-card-carousel__controls {
                display: flex;
                justify-content: center;
                gap: 1rem;
                padding-top: 0.5rem;
            }
        }

        .s25-card-carousel__arrow {
            flex: 0 0 auto;
            align-self: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 2.75rem;
            min-width: 2.75rem;
            border: 1px solid var(--color--border-bold);
            border-radius: 50%;
            background: var(--color--surface-bg);
            color: var(--color--text);
            cursor: pointer;
        }

        .s25-card-carousel__arrow:focus-visible {
            outline: none;
            box-shadow: var(--shadow--inner-focus);
        }

        .s25-card-carousel__empty {
            padding: 0.5rem;
        }
    `,
})
export class S25CardCarouselComponent {
    readonly items = contentChildren(forwardRef(() => S25CardCarouselItemDirective));

    /**
     * Accessible name for the scroll container. Required — a generic default would name every
     * landmark in 25Live identically, making landmark navigation useless.
     */
    readonly ariaLabel = input.required<string>();
    /** Noun used to build the default arrow labels: "Previous {itemNoun}" / "Next {itemNoun}". */
    readonly itemNoun = input("item");
    /** Override the "Previous" button label. Defaults to "Previous {itemNoun}". */
    readonly previousLabel = input<string>();
    /** Override the "Next" button label. Defaults to "Next {itemNoun}". */
    readonly nextLabel = input<string>();
    /** Stretches the strip to fill its host's height on mobile (flex column). */
    readonly fillHeight = input(true);

    private readonly scrollContainerRef = viewChild<ElementRef<HTMLElement>>("scrollContainer");

    private readonly reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    private readonly mobileQuery = window.matchMedia("(max-width: 930px)");

    readonly reducedMotion = signal(this.reduceMotionQuery.matches);
    readonly isMobile = signal(this.mobileQuery.matches);

    readonly isEmpty = computed(() => this.items().length === 0);
    readonly isMultiple = computed(() => this.items().length >= 2);
    readonly containerRole = computed(() => (this.isMultiple() ? "list" : "region"));
    readonly containerTabIndex = computed(() => (this.isMultiple() ? 0 : null));
    readonly showControls = computed(() => this.isMobile() && this.isMultiple());
    readonly previousAriaLabel = computed(() => this.previousLabel() ?? `Previous ${this.itemNoun()}`);
    readonly nextAriaLabel = computed(() => this.nextLabel() ?? `Next ${this.itemNoun()}`);

    constructor() {
        effect((onCleanup) => {
            const onReducedMotionChange = (event: MediaQueryListEvent) => this.reducedMotion.set(event.matches);
            const onMobileChange = (event: MediaQueryListEvent) => this.isMobile.set(event.matches);
            this.reduceMotionQuery.addEventListener("change", onReducedMotionChange);
            this.mobileQuery.addEventListener("change", onMobileChange);

            onCleanup(() => {
                this.reduceMotionQuery.removeEventListener("change", onReducedMotionChange);
                this.mobileQuery.removeEventListener("change", onMobileChange);
            });
        });

        if (isDevMode()) {
            afterNextRender(() => this.assertProjectionShape());
            effect(() => {
                this.items();
                setTimeout(() => this.assertProjectionShape());
            });
        }
    }

    scrollByCards(direction: 1 | -1): void {
        const scrollEl = this.scrollContainerRef()?.nativeElement;
        if (!scrollEl) return;
        const firstCard = scrollEl.querySelector<HTMLElement>("[data-s25-card-carousel-item]");
        const gapPx = parseFloat(getComputedStyle(scrollEl).columnGap) || 0;
        const step = firstCard ? firstCard.offsetWidth + gapPx : scrollEl.clientWidth * 0.8;
        scrollEl.scrollBy({ left: direction * step, behavior: this.reducedMotion() ? "auto" : "smooth" });
    }

    /**
     * Dev-mode DOM scan: every element child of the scroll container must carry
     * [data-s25-card-carousel-item] (set by S25CardCarouselItemDirective). A contentChildren query would
     * still find directives nested inside a wrapper element and report success, while the browser
     * would see broken list/listitem parentage — hence the DOM scan.
     */
    private assertProjectionShape(): void {
        const scrollEl = this.scrollContainerRef()?.nativeElement;
        if (!scrollEl || !this.isMultiple()) return;
        const unmarked = Array.from(scrollEl.children).filter(
            (child) => !("s25CardCarouselItem" in (child as HTMLElement).dataset),
        );
        if (unmarked.length) {
            console.error(
                "S25CardCarouselComponent: some children lack [s25-ng-card-carousel-item]. " +
                    "role=list/listitem parentage is broken. Apply the directive to every card.",
                unmarked,
            );
        }

        if (this.isMultiple()) {
            const firstItem = scrollEl.querySelector<HTMLElement>("[data-s25-card-carousel-item]");
            if (firstItem && firstItem.offsetWidth > 0 && Math.abs(firstItem.offsetWidth - scrollEl.clientWidth) < 4) {
                console.error(
                    "S25CardCarouselComponent: first card fills the full container width — no peek for desktop " +
                        "users. Override --s25-card-carousel-item-width or reduce card width.",
                    scrollEl,
                );
            }
        }
    }
}
